import { MentorMatchingSimulator } from "@/components/admin/mentor-matching-simulator";
import { AdjustOverrideDateDialog } from "@/components/mentors/adjust-override-date-dialog";
import { MatchingOverrideDialog } from "@/components/mentors/matching-override-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { ConfirmActionDialog } from "@/components/ui/confirm-action-dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/use-toast";
import {
  MentorSlot,
  MentorSlotThresholds,
  useEndMatchingOverride,
  useGetAllMentorSlots,
} from "@/lib/api/user";
import { useCan } from "@/lib/casl/use-can";
import { requirePermission } from "@/lib/casl/route-guards";
import { useSignedMedia } from "@/lib/hooks/use-signed-media";
import { cn, getInitials } from "@/lib/utils";
import { createFileRoute, Link } from "@tanstack/react-router";
import { format, parseISO } from "date-fns";
import {
  ChevronLeft,
  ChevronRight,
  Loader2,
  Search as SearchIcon,
  ShieldAlert,
} from "lucide-react";
import { useMemo, useState } from "react";

export const Route = createFileRoute("/_authenticated/mentors/")({
  component: Mentors,
  beforeLoad: ({ context }) => {
    requirePermission(context.user, "read", "mentor-slots");
  },
});

const STATUS_TABS: { value: string; label: string }[] = [
  { value: "all", label: "All" },
  { value: "open", label: "Open" },
  { value: "auto_paused", label: "Paused" },
  { value: "manual_override", label: "Override" },
];

// paused override -> paused -> open override -> open
function statusRank(mentorSlot: MentorSlot) {
  const { matching_status, override } = mentorSlot.slot_config;
  if (matching_status === "manual_override") {
    return override?.direction === "pause" ? 0 : 2;
  }
  if (matching_status === "auto_paused") {
    return 1;
  }
  return 3;
}

const PAUSE_REASON_LABELS: Record<string, string> = {
  responsiveness: "Slow response time",
  capacity: "Over capacity",
  ramp_limit: "Ramp limit",
  max_daily: "Daily cap reached",
  planned_pto: "Planned PTO",
  emergency_time_off: "Emergency Time Off",
  other_time_off: "Other",
};

const PAGE_SIZE_OPTIONS = [10, 20, 30, 50, 100];

function Mentors() {
  const { data, isPending } = useGetAllMentorSlots();
  const mentorSlots = data?.mentor_slots;
  const thresholds = data?.thresholds;
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [cohortFilter, setCohortFilter] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  // dialog lives at page level so the refetch after the mutation can
  // re-sort/refilter the table without unmounting the success screen
  const [overrideSlot, setOverrideSlot] = useState<MentorSlot | null>(null);

  const cohorts = useMemo(() => {
    const names = new Set<string>();
    mentorSlots?.forEach((slot) =>
      slot.group_names?.forEach((name) => names.add(name)),
    );
    return [...names].sort();
  }, [mentorSlots]);

  // the API already scopes to users holding the mentor system role, so no
  // client-side role filtering is needed
  const baseFilteredSlots = useMemo(() => {
    const query = search.trim().toLowerCase();
    return mentorSlots?.filter((mentorSlot) => {
      if (query && !mentorSlot.mentor.name?.toLowerCase().includes(query)) {
        return false;
      }
      if (
        cohortFilter !== "all" &&
        !mentorSlot.group_names?.includes(cohortFilter)
      ) {
        return false;
      }
      return true;
    });
  }, [mentorSlots, cohortFilter, search]);

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {
      all: baseFilteredSlots?.length ?? 0,
      open: 0,
      auto_paused: 0,
      manual_override: 0,
    };
    baseFilteredSlots?.forEach((slot) => {
      counts[slot.slot_config.matching_status] =
        (counts[slot.slot_config.matching_status] ?? 0) + 1;
    });
    return counts;
  }, [baseFilteredSlots]);

  const filteredMentorSlots = useMemo(() => {
    const slots =
      statusFilter === "all"
        ? baseFilteredSlots
        : baseFilteredSlots?.filter(
            (slot) => slot.slot_config.matching_status === statusFilter,
          );
    return slots?.slice().sort((a, b) => statusRank(a) - statusRank(b));
  }, [baseFilteredSlots, statusFilter]);

  const pageCount = Math.max(
    1,
    Math.ceil((filteredMentorSlots?.length ?? 0) / pageSize),
  );
  const currentPage = Math.min(pageIndex, pageCount - 1);

  const paginatedMentorSlots = useMemo(
    () =>
      filteredMentorSlots?.slice(
        currentPage * pageSize,
        (currentPage + 1) * pageSize,
      ),
    [filteredMentorSlots, currentPage, pageSize],
  );

  if (isPending) {
    return (
      <div className="flex h-[500px] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-6 pb-24 pt-10">
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight">Mentor Slots</h1>
        <p className="mt-1 text-sm text-gray-400">
          Updated daily at 9:00 AM ET
        </p>
      </div>

      <div className="mb-4 flex items-center justify-between py-2">
        <div className="flex items-center gap-1.5">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.value}
              type="button"
              onClick={() => {
                setStatusFilter(tab.value);
                setPageIndex(0);
              }}
              className={cn(
                "rounded-full px-3 py-1 text-xs transition-colors",
                statusFilter === tab.value
                  ? "bg-gray-900 text-white"
                  : "text-gray-500 hover:bg-gray-100",
              )}
            >
              {tab.label} {statusCounts[tab.value] ?? 0}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          {/* Input's internal wrapper is w-full, so constrain from outside */}
          <div className="w-[220px] shrink-0">
            <Input
              placeholder="Search mentors..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPageIndex(0);
              }}
              startIcon={<SearchIcon className="h-4 w-4" />}
            />
          </div>
          {cohorts.length > 0 && (
            <Select
              value={cohortFilter}
              onValueChange={(value) => {
                setCohortFilter(value);
                setPageIndex(0);
              }}
            >
              <SelectTrigger className="w-[150px] shrink-0">
                <SelectValue placeholder="Cohort" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All cohorts</SelectItem>
                {cohorts.map((cohort) => (
                  <SelectItem key={cohort} value={cohort}>
                    {cohort}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <MentorMatchingSimulator />
        </div>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="text-[10px] uppercase tracking-wide">
              Mentor
            </TableHead>
            <TableHead className="text-[10px] uppercase tracking-wide">
              Status
            </TableHead>
            <TableHead className="text-[10px] uppercase tracking-wide">
              Reason
            </TableHead>
            <TableHead className="text-[10px] uppercase tracking-wide">
              Caseload
            </TableHead>
            <TableHead className="text-[10px] uppercase tracking-wide">
              Resp. time
            </TableHead>
            <TableHead className="text-right text-[10px] uppercase tracking-wide">
              Actions
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {paginatedMentorSlots?.map((mentorSlot) => (
            <MentorRow
              key={mentorSlot.mentor.id}
              mentorSlot={mentorSlot}
              thresholds={thresholds}
              onOverride={() => setOverrideSlot(mentorSlot)}
            />
          ))}
        </TableBody>
      </Table>

      {overrideSlot && (
        <MatchingOverrideDialog
          mentorSlot={overrideSlot}
          isOpen
          onClose={() => setOverrideSlot(null)}
          forceOpenAllocation={thresholds?.force_open_teen_allocation}
        />
      )}

      <div className="mt-4 flex items-center justify-end gap-6">
        <div className="flex items-center gap-2">
          <p className="whitespace-nowrap text-sm font-medium">Rows per page</p>
          <Select
            value={`${pageSize}`}
            onValueChange={(value) => {
              setPageSize(Number(value));
              setPageIndex(0);
            }}
          >
            <SelectTrigger className="h-8 w-[5rem]">
              <SelectValue placeholder={pageSize} />
            </SelectTrigger>
            <SelectContent side="top">
              {PAGE_SIZE_OPTIONS.map((size) => (
                <SelectItem key={size} value={`${size}`}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="text-sm font-medium">
          Page {currentPage + 1} of {pageCount}
        </div>
        <div className="flex items-center gap-2">
          <Button
            aria-label="Go to previous page"
            variant="outline"
            size="icon"
            className="size-8"
            onClick={() => setPageIndex(currentPage - 1)}
            disabled={currentPage === 0}
          >
            <ChevronLeft className="size-4" aria-hidden="true" />
          </Button>
          <Button
            aria-label="Go to next page"
            variant="outline"
            size="icon"
            className="size-8"
            onClick={() => setPageIndex(currentPage + 1)}
            disabled={currentPage >= pageCount - 1}
          >
            <ChevronRight className="size-4" aria-hidden="true" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function isSystemOverride(mentorSlot: MentorSlot) {
  return (
    mentorSlot.slot_config.matching_status === "manual_override" &&
    mentorSlot.slot_config.override?.type === "system"
  );
}

function StatusCell({ mentorSlot }: { mentorSlot: MentorSlot }) {
  const { matching_status, override } = mentorSlot.slot_config;

  // lead overrides get the indigo shield treatment; system overrides look
  // like a regular open mentor (the reason column explains why)
  if (matching_status === "manual_override" && override?.type === "manual") {
    const label = override.direction === "pause" ? "Paused" : "Open";
    return (
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center gap-1.5">
          <ShieldAlert className="h-3 w-3 text-[#5e65fc]" />
          <span className="text-xs font-medium text-[#5e65fc]">{label}</span>
        </div>
        {override.direction === "pause" && override.endDate && (
          <span className="text-[10px] text-gray-500">
            Until {format(parseISO(override.endDate.slice(0, 10)), "MMM d, yyyy")}
          </span>
        )}
      </div>
    );
  }

  const isOpen = matching_status === "open" || isSystemOverride(mentorSlot);

  return (
    <div className="flex items-center gap-1.5">
      <span
        className={cn(
          "h-1.5 w-1.5 rounded-full",
          isOpen ? "bg-green-500" : "bg-gray-400",
        )}
      />
      <span
        className={cn("text-xs", isOpen ? "text-gray-600" : "text-gray-400")}
      >
        {isOpen ? "Open" : "Paused"}
      </span>
    </div>
  );
}

function isStateMetricOverride(
  mentorSlot: MentorSlot,
  thresholds?: MentorSlotThresholds,
) {
  const effective = mentorSlot.metrics.effective_max_caseload;
  const globalCap = thresholds?.capacity_max_activity_teens;
  return (
    mentorSlot.slot_config.pause_reason === "capacity" &&
    effective != null &&
    globalCap != null &&
    effective < globalCap
  );
}

function stateMetricOverrideLabel(mentorSlot: MentorSlot) {
  const cap = mentorSlot.metrics.effective_max_caseload;
  const groups = mentorSlot.group_names ?? [];
  const group = groups.length > 0 && groups.length <= 2 ? groups.join(", ") : null;
  if (group && cap != null) {
    return `Over capacity — ${group} cap (${cap})`;
  }
  if (cap != null) {
    return `Over capacity — state cap (${cap})`;
  }
  return "Over capacity — state metric override";
}

function ReasonCell({
  mentorSlot,
  thresholds,
}: {
  mentorSlot: MentorSlot;
  thresholds?: MentorSlotThresholds;
}) {
  const { matching_status, pause_reason, override } = mentorSlot.slot_config;
  const { ramp_week, matches_since_override } = mentorSlot.metrics;

  if (matching_status === "manual_override" && override?.type === "manual") {
    return (
      <div className="flex flex-col gap-1">
        <span className="text-xs text-gray-600">Manual override</span>
        {override.reason && (
          <span className="text-[10px] text-gray-500">{override.reason}</span>
        )}
      </div>
    );
  }

  if (isSystemOverride(mentorSlot)) {
    const allocation = override?.allocation ?? 0;
    const used = matches_since_override ?? 0;
    return (
      <div className="flex flex-col gap-1">
        <span className="text-xs text-gray-600">More mentors needed</span>
        {allocation > 0 && (
          <div className="flex items-center gap-1.5">
            <div className="h-1 w-10 rounded-full bg-gray-100">
              <div
                className="h-1 rounded-full bg-gray-400"
                style={{
                  width: `${Math.min((used / allocation) * 100, 100)}%`,
                }}
              />
            </div>
            <span className="text-[10px] text-gray-500">
              {used}/{allocation} teens
            </span>
          </div>
        )}
      </div>
    );
  }

  if (matching_status === "auto_paused" && pause_reason) {
    const label = isStateMetricOverride(mentorSlot, thresholds)
      ? stateMetricOverrideLabel(mentorSlot)
      : pause_reason === "ramp_limit" && ramp_week != null
        ? `Ramp limit — week ${ramp_week}`
        : (PAUSE_REASON_LABELS[pause_reason] ?? pause_reason);
    return <span className="text-xs text-gray-600">{label}</span>;
  }

  return null;
}

function CaseloadCell({
  mentorSlot,
  thresholds,
}: {
  mentorSlot: MentorSlot;
  thresholds?: MentorSlotThresholds;
}) {
  // live active-teen count (latest mentorship_logs status) — the same number
  // the capacity pause check evaluates
  const caseload = mentorSlot.metrics.active_teens;
  // per-mentor cap (group capacity overrides), falling back to the global one
  const capacity =
    mentorSlot.metrics.effective_max_caseload ??
    thresholds?.capacity_max_activity_teens ??
    null;

  if (caseload == null) {
    return <span className="text-sm text-gray-400">—</span>;
  }

  const atCapacity = capacity != null && caseload >= capacity;

  return (
    <div className="flex items-center gap-2">
      <span
        className={cn(
          "w-6 text-right text-sm tabular-nums",
          atCapacity ? "text-red-600" : "text-gray-900",
        )}
      >
        {caseload}
      </span>
      {capacity != null && (
        <div className="h-1.5 w-[72px] rounded-full bg-gray-100">
          <div
            className={cn(
              "h-1.5 rounded-full",
              atCapacity ? "bg-red-600" : "bg-gray-300",
            )}
            style={{
              width: `${Math.min((caseload / capacity) * 100, 100)}%`,
            }}
          />
        </div>
      )}
    </div>
  );
}

function ResponseTimeCell({
  mentorSlot,
  thresholds,
}: {
  mentorSlot: MentorSlot;
  thresholds?: MentorSlotThresholds;
}) {
  const responseTime = mentorSlot.metrics.median_response_time_min;
  if (responseTime == null) {
    return <span className="text-sm text-gray-400">—</span>;
  }

  const limit = thresholds?.response_time_min ?? null;
  const aboveLimit = limit != null && responseTime >= limit;

  return (
    <span
      className={cn(
        "text-sm tabular-nums",
        aboveLimit ? "text-red-600" : "text-gray-600",
      )}
    >
      {Math.round(responseTime * 10) / 10}m
    </span>
  );
}

function MentorRow({
  mentorSlot,
  thresholds,
  onOverride,
}: {
  mentorSlot: MentorSlot;
  thresholds?: MentorSlotThresholds;
  onOverride: () => void;
}) {
  const can = useCan();
  const canUpdate = can("update", "mentor-slots");
  const endOverride = useEndMatchingOverride();
  const { toast } = useToast();
  const [isEndOverrideOpen, setIsEndOverrideOpen] = useState(false);
  const [isAdjustDateOpen, setIsAdjustDateOpen] = useState(false);

  const avatarUrl = useSignedMedia(mentorSlot.mentor.profile_picture_url);
  const { matching_status, override } = mentorSlot.slot_config;
  const rampWeek = mentorSlot.metrics.ramp_week;
  const isManualOverride =
    matching_status === "manual_override" && override?.type === "manual";
  const isManualPauseOverride =
    isManualOverride && override?.direction === "pause";

  const handleEndOverride = async () => {
    try {
      await endOverride.mutateAsync({ userId: mentorSlot.mentor.id });
      toast({
        title: "Success",
        description: `Override ended, ${mentorSlot.mentor.name} is back to automatic matching`,
      });
      setIsEndOverrideOpen(false);
    } catch (_error) {
      toast({
        title: "Error",
        description: "Failed to end override",
        variant: "destructive",
      });
    }
  };

  return (
    <TableRow>
      <TableCell>
        <Link
          to="/users/$userId"
          params={{ userId: mentorSlot.mentor.id }}
          className="flex items-center gap-2 hover:underline"
        >
          <Avatar className="h-6 w-6">
            <AvatarImage src={avatarUrl?.url ?? ""} />
            <AvatarFallback className="text-[10px]">
              {getInitials(mentorSlot.mentor.name)}
            </AvatarFallback>
          </Avatar>
          <span className="text-sm">{mentorSlot.mentor.name}</span>
          {rampWeek != null && (
            <span className="text-[10px] text-gray-400">Week {rampWeek}</span>
          )}
        </Link>
      </TableCell>
      <TableCell>
        <StatusCell mentorSlot={mentorSlot} />
      </TableCell>
      <TableCell>
        <ReasonCell mentorSlot={mentorSlot} thresholds={thresholds} />
      </TableCell>
      <TableCell>
        <CaseloadCell mentorSlot={mentorSlot} thresholds={thresholds} />
      </TableCell>
      <TableCell>
        <ResponseTimeCell mentorSlot={mentorSlot} thresholds={thresholds} />
      </TableCell>
      <TableCell className="text-right">
        {canUpdate &&
          (isManualOverride ? (
            <div className="flex justify-end gap-2">
              {isManualPauseOverride && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsAdjustDateOpen(true)}
                >
                  Adjust date
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsEndOverrideOpen(true)}
              >
                End override
              </Button>
            </div>
          ) : (
            <Button variant="outline" size="sm" onClick={onOverride}>
              Override
            </Button>
          ))}
      </TableCell>

      <ConfirmActionDialog
        isOpen={isEndOverrideOpen}
        onClose={() => setIsEndOverrideOpen(false)}
        onConfirm={handleEndOverride}
        title="End override"
        description={`The automatic matching status will take over for ${mentorSlot.mentor.name} immediately.`}
        loading={endOverride.isPending}
        confirmVariant="default"
        confirmText="End Override"
      />

      {isAdjustDateOpen && (
        <AdjustOverrideDateDialog
          mentorSlot={mentorSlot}
          isOpen
          onClose={() => setIsAdjustDateOpen(false)}
        />
      )}
    </TableRow>
  );
}
